import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../../../components/Navbar';
import { useNavigate, useParams } from 'react-router-dom';
import { createAllPhaseMenuGroups } from '../../../../utils/createAllPhaseMenuGroups';
import axiosInstance from '../../../../utils/axiosInstance';
import config from '../../../../config.json';
import url from '../../../../url.json';
import io from 'socket.io-client';
import {
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Switch,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Book as BookIcon,
  Apps as AppsIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Home as HomeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Lightbulb as LightbulbIcon,
  Forum as ForumIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';


const sortOptions = ['最新', '最舊', '精選優先'];

export default function ClassApplicationCrossGroupDiscussion() {
  const { id: courseDispatchId } = useParams();
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [sortBy, setSortBy] = useState('最新');
  const [discussions, setDiscussions] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [courseDispatch, setCourseDispatch] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [ws, setWs] = useState(null); // WebSocket 連接

  // 載入課程派發資料和小組列表
  const fetchCourseDispatchAndGroups = async () => {
    try {
      setLoading(true);
      setError(null);

      // 獲取課程派發詳情
      const dispatchResponse = await axiosInstance.get(`${config.getCourseDispatchDetail}${courseDispatchId}/detail`);
      if (dispatchResponse.data.success) {
        const detailData = dispatchResponse.data.data;
        const appInfo = detailData.applicationInfo || {};
        setCourseDispatch(appInfo);
      }

      // 獲取課程小組列表
      const groupsResponse = await axiosInstance.get(`${config.getCourseDispatchGroups}${courseDispatchId}/groups`);
      if (groupsResponse.data.success) {
        const groups = groupsResponse.data.data || [];
        setAvailableGroups(groups);
        
        // 設定預設報告小組（第一個小組）
        if (groups.length > 0 && !selectedGroup) {
          setSelectedGroup(groups[0].id);
        }
      }
    } catch (err) {
      console.error('載入課程資料失敗:', err);
      setError('載入課程資料失敗');
    } finally {
      setLoading(false);
    }
  };

  // 載入跨組討論留言
  const fetchComments = async (reportingGroupId) => {
    try {
      setLoading(true);
      setError(null);

      const url = reportingGroupId 
        ? `${config.getCrossGroupDiscussion}${courseDispatchId}/${reportingGroupId}`
        : `${config.getCrossGroupDiscussion}${courseDispatchId}`;
      
      const response = await axiosInstance.get(url);
      if (response.data.success) {
        setDiscussions(response.data.data || []);
      }
    } catch (err) {
      console.error('載入跨組討論留言失敗:', err);
      setError('載入留言失敗');
    } finally {
      setLoading(false);
    }
  };

  // 初始化載入資料
  useEffect(() => {
    if (courseDispatchId) {
      fetchCourseDispatchAndGroups();
    }
  }, [courseDispatchId]);

  // 當選擇的報告小組改變時，重新載入留言
  useEffect(() => {
    if (selectedGroup) {
      fetchComments(selectedGroup);
    }
  }, [selectedGroup]);

  // 初始化 WebSocket 連接（跨組討論）
  useEffect(() => {
    if (courseDispatchId) {
      console.log('[老師端跨組討論] 初始化 WebSocket 連接，courseDispatchId:', courseDispatchId);
      console.log('[老師端跨組討論] WebSocket 連接 URL:', url.socketioHost);
      console.log('[老師端跨組討論] WebSocket 連接路徑:', '/s/socket.io');
      
      const socket = io.connect(url.socketioHost, { 
        path: '/s/socket.io',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });
      setWs(socket);

      socket.on('connect', () => {
        console.log('[老師端跨組討論] WebSocket 已連接，socket.id:', socket.id);
      });

      socket.on('connect_error', (error) => {
        console.error('[老師端跨組討論] WebSocket 連接錯誤:', error);
        console.error('[老師端跨組討論] 連接失敗的 URL:', url.socketioHost);
      });

      socket.on('disconnect', (reason) => {
        console.log('[老師端跨組討論] WebSocket 已斷開，原因:', reason);
      });

      // 清理函數
      return () => {
        console.log('[老師端跨組討論] 斷開 WebSocket 連接');
        socket.disconnect();
      };
    }
  }, [courseDispatchId]);

  // WebSocket 即時更新監聽（跨組討論新留言）
  useEffect(() => {
    if (ws && courseDispatchId) {
      console.log('[老師端跨組討論] 設置 WebSocket 監聽，courseDispatchId:', courseDispatchId);

      // 監聽跨組討論新留言事件
      const handleNewComment = (commentData) => {
        console.log('🔔 [老師端跨組討論 WebSocket] ========== 收到新留言事件 ==========');
        console.log('  - 完整數據:', JSON.stringify(commentData, null, 2));
        console.log('  - 留言 ID:', commentData.id);
        console.log('  - 留言內容:', commentData.content);
        console.log('  - 報告小組 ID:', commentData.reporting_group_id);
        console.log('  - 課程派發 ID:', commentData.course_dispatch_id);

        // 檢查是否與當前選擇的報告小組相關，或者重新載入當前小組的留言
        if (commentData.course_dispatch_id == courseDispatchId) {
          console.log('✅ [老師端跨組討論 WebSocket] 檢測到新留言，更新本地狀態');
          
          // 如果當前有選擇報告小組，且新留言屬於該小組，則更新留言列表
          if (selectedGroup && commentData.reporting_group_id == selectedGroup) {
            console.log('  - 新留言屬於當前選擇的報告小組，更新留言列表');
            // 將新留言添加到本地狀態
            setDiscussions(prev => {
              // 檢查是否已存在（避免重複）
              const exists = prev.some(c => c.id === commentData.id);
              if (exists) {
                console.log('  - 留言已存在，跳過添加');
                return prev;
              }
              console.log('  - 添加新留言到列表');
              return [commentData, ...prev];
            });
          } else if (selectedGroup) {
            // 如果當前選擇的小組與新留言的小組不同，重新載入當前小組的留言（保持當前視角）
            console.log('  - 新留言屬於其他小組，保持當前視角不變');
          }
        } else {
          console.log('⚠️ [老師端跨組討論 WebSocket] 忽略不同課程的留言');
        }
        console.log('========== 新留言事件處理結束 ==========');
      };

      // 監聽跨組討論專用事件
      ws.on(`cross-group-comment-${courseDispatchId}`, handleNewComment);

      // 也監聽通用的 node-recieve 事件（後端可能通過此事件廣播）
      const handleNewNode = (body) => {
        // 檢查是否為跨組討論類型的節點
        if (body.wall_type === 'cross_group' && body.course_dispatch_id == courseDispatchId) {
          console.log('🔔 [老師端跨組討論 WebSocket] 透過 node-recieve 收到跨組討論新留言');
          handleNewComment(body);
        }
      };

      ws.on(`node-recieve-${courseDispatchId}`, handleNewNode);

      // 清理函數
      return () => {
        console.log('[老師端跨組討論] 移除 WebSocket 監聽');
        if (ws) {
          ws.off(`cross-group-comment-${courseDispatchId}`, handleNewComment);
          ws.off(`node-recieve-${courseDispatchId}`, handleNewNode);
        }
      };
    }
  }, [ws, courseDispatchId, selectedGroup]);

  const currentGroup = availableGroups.find(g => g.id === selectedGroup);
  const sortedDiscussions = [...discussions].sort((a, b) => {
    if (sortBy === '精選優先') {
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
    }
    if (sortBy === '最新') return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === '最舊') return new Date(a.created_at) - new Date(b.created_at);
    return 0;
  });

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('implementation', 'cross-group', navigate, courseDispatchId);

  // 用於防止重複點擊的 ref
  const isSubmittingRef = useRef(false);

  // 新增討論訊息（老師端也可以發表留言）
  const handleAddMessage = async () => {
    if (!newMessage.trim() || !selectedGroup) return;
    
    // 防重複點擊：如果正在提交，直接返回
    if (isSubmittingRef.current) {
      console.log('[handleAddMessage] 正在提交中，忽略重複點擊');
      return;
    }
    
    try {
      isSubmittingRef.current = true;
      setLoading(true);
      const response = await axiosInstance.post(config.createCrossGroupComment, {
        course_dispatch_id: courseDispatchId,
        reporting_group_id: selectedGroup,
        content: newMessage
      });

      if (response.data.success) {
        // 重新載入留言列表
        await fetchComments(selectedGroup);
        setNewMessage('');
        setSnackbar({ open: true, message: '訊息已發送！', severity: 'success' });
      }
    } catch (err) {
      console.error('發表留言失敗:', err);
      setSnackbar({ open: true, message: '發表留言失敗', severity: 'error' });
    } finally {
      setLoading(false);
      // 延遲清除標記，避免快速重複點擊
      setTimeout(() => {
        isSubmittingRef.current = false;
      }, 1000);
    }
  };

  // 切換精選
  const toggleHighlight = async (commentId, currentFeatured) => {
    try {
      const response = await axiosInstance.put(`${config.updateCrossGroupComment}${commentId}`, {
        is_featured: !currentFeatured
      });

      if (response.data.success) {
        // 重新載入留言列表
        await fetchComments(selectedGroup);
        setSnackbar({ 
          open: true, 
          message: !currentFeatured ? '已標記為精選' : '已取消精選', 
          severity: 'success' 
        });
      }
    } catch (err) {
      console.error('更新精選狀態失敗:', err);
      setSnackbar({ open: true, message: '操作失敗', severity: 'error' });
    }
  };

  // 刪除訊息
  const deleteMessage = async (commentId) => {
    if (!window.confirm('確定要刪除這個訊息嗎？')) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.delete(`${config.deleteCrossGroupComment}${commentId}`);

      if (response.data.success) {
        // 重新載入留言列表
        await fetchComments(selectedGroup);
    setSnackbar({ open: true, message: '訊息已刪除！', severity: 'success' });
      }
    } catch (err) {
      console.error('刪除留言失敗:', err);
      setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="teacher-layout class-application-cross-group-discussion-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="implementation"
          currentCourseId={courseDispatchId}
          onPhaseChange={(phase) => {
            if (phase === 'preparation') {
              navigate('/teacher/preparation');
            } else if (phase === 'implementation') {
              navigate('/teacher/implementation');
            } else if (phase === 'diagnosis') {
              navigate('/teacher/diagnosis');
            }
          }}
          onCourseChange={(newCourseId) => {
            const currentPath = window.location.pathname;
            const newPath = currentPath.replace(/\/\d+(\/|$)/, `/${newCourseId}$1`);
            navigate(newPath);
          }}
        />
        
        <div className="class-header-row">
          <h1 className="main-title">
            跨組討論
            {courseDispatch && (
              <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1, display: 'inline' }}>
                {courseDispatch.title || courseDispatch.activity_name || '課程'}
              </Typography>
            )}
          </h1>
        </div>

        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ height: 'calc(100vh - 200px)' }}>
          {/* 左側控制面板 */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', overflow: 'auto' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#1e4d5f', fontWeight: 'bold', mb: 2 }}>
                  💬 報告小組管理
                </Typography>

                {/* 小組下拉選擇 */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>選擇報告小組</InputLabel>
                  <Select
                    value={selectedGroup || ''}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    label="選擇報告小組"
                    disabled={loading || !availableGroups.length}
                  >
                    <MenuItem value="">
                      <em>全部小組</em>
                    </MenuItem>
                    {availableGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <GroupIcon sx={{ color: '#1e4d5f', fontSize: '16px' }} />
                          <span style={{ flex: 1 }}>{group.groupName || group.name}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  排序控制
                </Typography>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>排序方式</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="排序方式"
                  >
                    {sortOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="body2" color="text.secondary">
                  共 {sortedDiscussions.length} 則討論
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* 右側討論區域 */}
          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* 參與討論區塊 - 移到上方 */}
              <CardContent sx={{ borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" gutterBottom>
                  參與討論
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="討論內容"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="請輸入您的提問、回應或評論..."
                  />
                  <Button
                    variant="contained"
                    className="btn-primary"
                    onClick={handleAddMessage}
                    disabled={!newMessage.trim() || loading || !selectedGroup}
                    startIcon={<SendIcon />}
                  >
                    發送
                  </Button>
                </Box>
              </CardContent>

              {/* 討論列表 */}
              <CardContent sx={{ flex: 1, overflow: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                  討論列表
                </Typography>
                
                {loading && sortedDiscussions.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : sortedDiscussions.length === 0 ? (
                  <Typography variant="body1" textAlign="center" color="text.secondary">
                    目前還沒有跨組討論
                  </Typography>
                ) : (
                  <List sx={{ p: 0 }}>
                    {sortedDiscussions.map((discussion) => (
                      <Card key={discussion.id} sx={{ mb: 1 }}>
                        <CardContent sx={{ py: 1, px: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main' }}>
                                {(discussion.author_name || discussion.Author?.name || '?').charAt(0)}
                              </Avatar>
                              <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                  {discussion.author_name || discussion.Author?.name || '未知'}
                              </Typography>
                                {/* 老師端不顯示作者所屬小組標籤 */}
                              </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(discussion.created_at).toLocaleString('zh-TW')}
                            </Typography>
                          </Box>
                          
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {discussion.content}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => toggleHighlight(discussion.id, discussion.is_featured)}
                              color={discussion.is_featured ? 'primary' : 'default'}
                            >
                              {discussion.is_featured ? <StarIcon /> : <StarBorderIcon />}
                            </IconButton>
                            {/* 顯示按讚數量（老師可見） */}
                            <Chip 
                              label={`${discussion.likes || 0}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                            
                            <IconButton
                              size="small"
                              onClick={() => deleteMessage(discussion.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                            
                            {discussion.is_featured && (
                              <Chip label="精選" color="warning" size="small" />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 通知訊息 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}
